import { DEFAULT_AUTO_NOTIFY_THRESHOLD } from "@sitescene/constant";
import { EventReanalysisKind, NONE_CLASS_ID } from "@sitescene/types";

export interface DeriveReanalysisKindParams {
	/** The class the event currently names, when it could be placed. */
	currentClassId?: string;
	/** The event's severity right now. */
	eventSeverity: number;
	/** The class staff say the clip really showed; `none` for nothing alertable. */
	target: { alertable: boolean; classId: string };
}

/**
 * Which correction a staff member's class makes of an event, or null when it
 * makes none. Both reanalysis panels call it to say what will be tested
 * before Submit, and the request route calls it to refuse a request that
 * corrects nothing, so the two can never disagree.
 *
 * - Nothing alertable on an alert → `falseAlarm`.
 * - Nothing alertable on an event that never alerted → nothing to correct.
 * - An alertable class on an event below the alert line → `missed`.
 * - A different alertable class on an alert → `misclassified`.
 * - The class the alert already names → nothing to correct.
 */
export function deriveReanalysisKind(
	params: DeriveReanalysisKindParams,
): EventReanalysisKind | null {
	const { currentClassId, eventSeverity, target } = params;
	const alerting = eventSeverity >= DEFAULT_AUTO_NOTIFY_THRESHOLD;
	const nothingAlertable =
		target.classId === NONE_CLASS_ID || !target.alertable;

	if (nothingAlertable) {
		return alerting ? EventReanalysisKind.FalseAlarm : null;
	}
	if (!alerting) return EventReanalysisKind.Missed;
	if (currentClassId === target.classId) return null;
	return EventReanalysisKind.Misclassified;
}
